import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';
import { CheckCircle, Circle, Play, Calendar, Dumbbell } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import type { ProgramDay, ProgramExercise } from '@/lib/programBuilder';

interface AssignedProgram {
  id: string;
  programId: string;
  programName: string;
  coachName: string;
  days: ProgramDay[];
}

export default function TodaysProgram() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const c = theme.colors;

  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<AssignedProgram | null>(null);
  const [completedExercises, setCompletedExercises] = useState<Set<string>>(new Set());

  const loadProgram = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      // Fetch active assignment for current user
      const { data: assignments, error: aErr } = await supabase
        .from('program_assignments')
        .select('id, program_id, coach_id')
        .eq('client_id', profile.id)
        .eq('status', 'active')
        .limit(1);

      if (aErr || !assignments || assignments.length === 0) {
        setProgram(null);
        setLoading(false);
        return;
      }

      const assignment = assignments[0];

      // Fetch program details
      const { data: prog, error: pErr } = await supabase
        .from('coach_programs')
        .select('id, name, days, coach_id')
        .eq('id', assignment.program_id)
        .single();

      if (pErr || !prog) {
        setProgram(null);
        setLoading(false);
        return;
      }

      // Fetch coach name
      let coachName = 'Your Coach';
      if (prog.coach_id) {
        const { data: coachProfile } = await supabase
          .from('profiles')
          .select('display_name, handle')
          .eq('id', prog.coach_id)
          .single();
        if (coachProfile) {
          coachName = coachProfile.display_name || coachProfile.handle || 'Your Coach';
        }
      }

      setProgram({
        id: assignment.id,
        programId: prog.id,
        programName: prog.name,
        coachName,
        days: (prog.days ?? []) as ProgramDay[],
      });
    } catch (err) {
      console.warn('[TodaysProgram] load error:', err);
      setProgram(null);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => { loadProgram(); }, [loadProgram]);

  const todayDayIndex = new Date().getDay(); // 0=Sun
  const todayDay = program?.days?.[todayDayIndex % (program.days.length || 1)] ?? null;
  const exercises = todayDay?.exercises ?? [];

  const toggleComplete = (exId: string) => {
    setCompletedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(exId)) next.delete(exId);
      else next.add(exId);
      return next;
    });
  };

  const handleStartWorkout = () => {
    if (!program) return;
    router.push({
      pathname: '/(tabs)/record',
      params: { programId: program.programId, programDayIndex: String(todayDayIndex % (program.days.length || 1)), view: 'log' },
    } as any);
  };

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: c.background }]}>
        <ActivityIndicator size="large" color={c.accent} />
      </View>
    );
  }

  if (!program) {
    return (
      <View style={[styles.center, { backgroundColor: c.background }]}>
        <Dumbbell size={48} color={c.textTertiary} />
        <Text style={[styles.emptyTitle, { color: c.text }]}>No program assigned yet</Text>
        <Text style={[styles.emptySubtitle, { color: c.textSecondary }]}>No program yet — browse templates to get started</Text>
      </View>
    );
  }

  if (exercises.length === 0) {
    return (
      <ScrollView style={{ flex: 1, backgroundColor: c.background }} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.programHeader, { borderBottomColor: c.border }]}>
          <Text style={[styles.programName, { color: c.text }]}>{program.programName}</Text>
          <Text style={[styles.coachName, { color: c.textSecondary }]}>Coach: {program.coachName}</Text>
        </View>
        <View style={styles.center}>
          <Calendar size={48} color={c.textTertiary} />
          <Text style={[styles.emptyTitle, { color: c.text }]}>Rest Day</Text>
          <Text style={[styles.emptySubtitle, { color: c.textSecondary }]}>No exercises scheduled today</Text>
        </View>
        <TouchableOpacity
          style={[styles.viewFullBtn, { borderColor: c.accent }]}
          onPress={() => router.push('/my-program' as any)}>
          <Text style={[styles.viewFullBtnText, { color: c.accent }]}>View Full Program</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: c.background }} contentContainerStyle={styles.scrollContent}>
      <View style={[styles.programHeader, { borderBottomColor: c.border }]}>
        <Text style={[styles.programName, { color: c.text }]}>{program.programName}</Text>
        <Text style={[styles.coachName, { color: c.textSecondary }]}>Coach: {program.coachName}</Text>
        {todayDay && <Text style={[styles.dayLabel, { color: c.accent }]}>{todayDay.name}</Text>}
      </View>

      {exercises.map((ex) => {
        const done = completedExercises.has(ex.id);
        return (
          <TouchableOpacity
            key={ex.id}
            style={[styles.exerciseCard, { backgroundColor: c.surface, borderColor: done ? c.accent : c.border, opacity: done ? 0.7 : 1 }]}
            onPress={() => toggleComplete(ex.id)}
            activeOpacity={0.7}>
            <View style={styles.exerciseCardLeft}>
              {done ? <CheckCircle size={22} color={c.accent} /> : <Circle size={22} color={c.border} />}
              <View style={{ flex: 1 }}>
                <Text style={[styles.exerciseName, { color: c.text, textDecorationLine: done ? 'line-through' : 'none' }]}>
                  {ex.exerciseName}
                </Text>
                <Text style={[styles.exerciseTargets, { color: c.textSecondary }]}>
                  {ex.targetSets} sets × {ex.targetReps} reps
                  {ex.targetWeight ? ` @ ${ex.targetWeight}` : ''}
                  {ex.rpe ? ` • RPE ${ex.rpe}` : ''}
                </Text>
                {ex.notes ? <Text style={[styles.exerciseNotes, { color: c.textTertiary }]}>{ex.notes}</Text> : null}
              </View>
            </View>
          </TouchableOpacity>
        );
      })}

      <TouchableOpacity style={[styles.startBtn, { backgroundColor: c.accent }]} onPress={handleStartWorkout}>
        <Play size={20} color={c.accentForeground} />
        <Text style={[styles.startBtnText, { color: c.accentForeground }]}>Start This Workout</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.viewFullBtn, { borderColor: c.accent }]}
        onPress={() => router.push('/my-program' as any)}>
        <Text style={[styles.viewFullBtnText, { color: c.accent }]}>View Full Program</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32, gap: 12 },
  scrollContent: { padding: 16, paddingBottom: 40 },
  programHeader: { borderBottomWidth: 1, paddingBottom: 12, marginBottom: 16, gap: 4 },
  programName: { fontSize: 22, fontFamily: TYPO.heading.bold },
  coachName: { fontSize: 14, fontFamily: TYPO.body.regular },
  dayLabel: { fontSize: 16, fontFamily: TYPO.heading.semibold, marginTop: 4 },
  emptyTitle: { fontSize: 18, fontFamily: TYPO.heading.bold },
  emptySubtitle: { fontSize: 14, fontFamily: TYPO.body.regular },
  exerciseCard: { flexDirection: 'row', borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 10 },
  exerciseCardLeft: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, flex: 1 },
  exerciseName: { fontSize: 16, fontFamily: TYPO.heading.semibold },
  exerciseTargets: { fontSize: 13, fontFamily: TYPO.body.regular, marginTop: 2 },
  exerciseNotes: { fontSize: 12, fontStyle: 'italic', marginTop: 4 },
  startBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 16, borderRadius: 2, marginTop: 8 },
  startBtnText: { fontSize: 17, fontFamily: TYPO.heading.bold },
  viewFullBtn: { alignItems: 'center', paddingVertical: 12, borderRadius: 2, borderWidth: 1, marginTop: 8 },
  viewFullBtnText: { fontSize: 14, fontWeight: '700' },
});
